import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Typography,
} from '@mui/joy';
import type { DooDoorStyle, DooDoorStyleSupplierMapping, DooSupplier, DoorStyleCollection, StatusFilter } from '../types';
import { DOOR_STYLE_COLLECTIONS } from '../data/doorStyleCollections';
import {
  createDoorStyle,
  createStyleMapping,
  deleteDoorStyle,
  deleteStyleMapping,
  doorStyleDependents,
  formatDependents,
  listAllStyleMappings,
  listDoorStyles,
  listMaterials,
  listStyleMappings,
  listSuppliers,
  updateDoorStyle,
  updateStyleMapping,
} from '../data/repository';
import { catalogueStatus, matchesStatusFilter } from '../data/status';
import { useAsyncList } from './NamedEntitySection';
import {
  ActionRow,
  AssetPathField,
  ConfirmDialog,
  DivestFields,
  Field,
  FormGrid,
  OracleTable,
  SectionCard,
  StatusChip,
  StatusFilterSelect,
  Toolbar,
  TruncatedText,
} from './FormControls';

export function DoorStylesSection({
  onOpenCompatibility,
}: {
  onOpenCompatibility: (doorStyleId: string) => void;
}) {
  const styles = useAsyncList(listDoorStyles);
  const materials = useAsyncList(listMaterials);
  const suppliers = useAsyncList(listSuppliers);
  const mappings = useAsyncList(listAllStyleMappings);
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<DooDoorStyle | null>(null);
  const [creating, setCreating] = useState(false);

  const visible = useMemo(
    () => styles.rows.filter((row) => matchesStatusFilter(row, filter)),
    [styles.rows, filter]
  );

  const { supplierNamesByDoorStyle, supplierStyleNamesByDoorStyle } = useMemo(
    () => buildSupplierMappingLookups(mappings.rows, suppliers.rows),
    [mappings.rows, suppliers.rows]
  );

  const materialName = (id: string | null) =>
    materials.rows.find((row) => row.material_id === id)?.material_name || '—';

  const reloadList = () => {
    styles.reload();
    suppliers.reload();
    mappings.reload();
  };

  if (editing || creating) {
    return (
      <DoorStyleEditor
        style={editing}
        materials={materials.rows}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        onSaved={() => {
          setEditing(null);
          setCreating(false);
          reloadList();
        }}
        onOpenCompatibility={onOpenCompatibility}
      />
    );
  }

  return (
    <Box>
      {styles.error ? <Alert color="danger" sx={{ mb: 2 }}>{styles.error}</Alert> : null}
      {mappings.error ? <Alert color="danger" sx={{ mb: 2 }}>{mappings.error}</Alert> : null}
      <Toolbar>
        <StatusFilterSelect value={filter} onChange={setFilter} />
        <Button onClick={() => setCreating(true)}>Add door style</Button>
      </Toolbar>
      <OracleTable>
        <thead>
          <tr>
            <th>Style name</th>
            <th>Supplier</th>
            <th>Supplier style name</th>
            <th>Collection</th>
            <th>Material</th>
            <th>Size range (W × H mm)</th>
            <th>Status</th>
            <th style={{ width: 180 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {styles.loading || mappings.loading || suppliers.loading ? (
            <tr><td colSpan={8}>Loading…</td></tr>
          ) : visible.length === 0 ? (
            <tr><td colSpan={8}>No door styles yet.</td></tr>
          ) : (
            visible.map((row) => {
              const supplierNames = supplierNamesByDoorStyle.get(row.door_style_id) || '—';
              const supplierStyleNames = supplierStyleNamesByDoorStyle.get(row.door_style_id) || '—';
              return (
                <tr key={row.door_style_id}>
                  <td>{row.keystone_style_name || '—'}</td>
                  <td>
                    <TruncatedText
                      value={supplierNames === '—' ? null : supplierNames}
                      maxWidth={160}
                    />
                  </td>
                  <td>
                    <TruncatedText
                      value={supplierStyleNames === '—' ? null : supplierStyleNames}
                      maxWidth={160}
                    />
                  </td>
                  <td>{row.collection || '—'}</td>
                  <td>{materialName(row.material_id)}</td>
                  <td>{formatSizeRange(row)}</td>
                  <td><StatusChip status={catalogueStatus(row)} /></td>
                  <td>
                    <ActionRow>
                      <Button size="sm" variant="plain" onClick={() => setEditing(row)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="plain"
                        onClick={() => onOpenCompatibility(row.door_style_id)}
                      >
                        Compatibility
                      </Button>
                    </ActionRow>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </OracleTable>
    </Box>
  );
}

function DoorStyleEditor({
  style,
  materials,
  onClose,
  onSaved,
  onOpenCompatibility,
}: {
  style: DooDoorStyle | null;
  materials: Array<{ material_id: string; material_name: string }>;
  onClose: () => void;
  onSaved: () => void;
  onOpenCompatibility: (doorStyleId: string) => void;
}) {
  const [name, setName] = useState(style?.keystone_style_name || '');
  const [collection, setCollection] = useState<DoorStyleCollection | null>(style?.collection ?? null);
  const [materialId, setMaterialId] = useState<string | null>(style?.material_id || null);
  const [minH, setMinH] = useState(String(style?.min_height_mm ?? ''));
  const [maxH, setMaxH] = useState(String(style?.max_height_mm ?? ''));
  const [minW, setMinW] = useState(String(style?.min_width_mm ?? ''));
  const [maxW, setMaxW] = useState(String(style?.max_width_mm ?? ''));
  const [letterbox, setLetterbox] = useState(String(style?.letterbox_positions ?? ''));
  const [assetPath, setAssetPath] = useState(style?.asset_path || '');
  const [divest, setDivest] = useState(Boolean(style?.divest));
  const [deprecatedDate, setDeprecatedDate] = useState(style?.deprecated_date?.slice(0, 10) || '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const savedId = style?.door_style_id ?? null;

  const payload = () => ({
    keystone_style_name: name.trim() || null,
    collection,
    material_id: materialId,
    min_height_mm: toInt(minH),
    max_height_mm: toInt(maxH),
    min_width_mm: toInt(minW),
    max_width_mm: toInt(maxW),
    letterbox_positions: toInt(letterbox),
    asset_path: assetPath.trim() || null,
    divest,
    deprecated_date: deprecatedDate || null,
  });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!collection) {
        setError('Collection is required.');
        return;
      }
      if (savedId) await updateDoorStyle(savedId, payload());
      else await createDoorStyle(payload());
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Button variant="plain" onClick={onClose} sx={{ mb: 1, px: 0 }}>
        ← All door styles
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography level="h3">
            {style ? style.keystone_style_name || 'Edit door style' : 'New door style'}
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500', mt: 0.5 }}>
            Configure catalogue details, dimensions, and supplier mappings.
          </Typography>
        </Box>
        <ActionRow>
          <Button loading={saving} onClick={save}>
            Save style
          </Button>
          {savedId ? (
            <>
              <Button variant="outlined" onClick={() => onOpenCompatibility(savedId)}>
                Edit compatibility
              </Button>
              <Button color="danger" variant="outlined" onClick={() => setDeleteOpen(true)}>
                Delete
              </Button>
            </>
          ) : null}
        </ActionRow>
      </Box>

      {error ? <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert> : null}

      <SectionCard title="Style details">
        <FormGrid columns={2}>
          <Field label="Style name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Collection">
            <Select
              placeholder="Choose collection"
              value={collection}
              onChange={(_, value) => setCollection((value as DoorStyleCollection) || null)}
            >
              {DOOR_STYLE_COLLECTIONS.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Field>
          <Field label="Material">
            <Select
              placeholder="Choose material"
              value={materialId}
              onChange={(_, value) => setMaterialId((value as string) || null)}
            >
              {materials.map((row) => (
                <Option key={row.material_id} value={row.material_id}>
                  {row.material_name}
                </Option>
              ))}
            </Select>
          </Field>
        </FormGrid>
      </SectionCard>

      <SectionCard title="Dimensions" subtitle="Minimum and maximum opening sizes in millimetres.">
        <FormGrid columns={4}>
          <Field label="Min width">
            <Input value={minW} onChange={(e) => setMinW(e.target.value)} slotProps={{ input: { inputMode: 'numeric' } }} />
          </Field>
          <Field label="Max width">
            <Input value={maxW} onChange={(e) => setMaxW(e.target.value)} slotProps={{ input: { inputMode: 'numeric' } }} />
          </Field>
          <Field label="Min height">
            <Input value={minH} onChange={(e) => setMinH(e.target.value)} slotProps={{ input: { inputMode: 'numeric' } }} />
          </Field>
          <Field label="Max height">
            <Input value={maxH} onChange={(e) => setMaxH(e.target.value)} slotProps={{ input: { inputMode: 'numeric' } }} />
          </Field>
        </FormGrid>
      </SectionCard>

      <SectionCard title="Configuration">
        <Field label="Letterbox positions">
          <Input value={letterbox} onChange={(e) => setLetterbox(e.target.value)} slotProps={{ input: { inputMode: 'numeric' } }} />
        </Field>
      </SectionCard>

      <SectionCard title="Artwork">
        <AssetPathField value={assetPath} onChange={setAssetPath} />
      </SectionCard>

      <SectionCard title="Lifecycle">
        <DivestFields
          divest={divest}
          deprecatedDate={deprecatedDate}
          onDivest={setDivest}
          onDeprecatedDate={setDeprecatedDate}
        />
      </SectionCard>

      <Divider sx={{ my: 2 }} />

      {savedId ? (
        <SupplierMappingsPanel doorStyleId={savedId} />
      ) : (
        <Alert color="neutral">Save the door style before adding supplier mappings.</Alert>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete door style?"
        message="Delete is blocked if mappings or compatibility rows still exist. Prefer deprecation or divest."
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          if (!savedId) return;
          try {
            const dependents = await doorStyleDependents(savedId);
            if (dependents.length) {
              setDeleteOpen(false);
              setError(
                `Cannot delete this door style while it still has ${formatDependents(dependents)}. Deprecate or divest it instead.`
              );
              return;
            }
            await deleteDoorStyle(savedId);
            onSaved();
          } catch (err) {
            setDeleteOpen(false);
            setError(err instanceof Error ? err.message : 'Delete failed');
          }
        }}
      />
    </Box>
  );
}

function SupplierMappingsPanel({ doorStyleId }: { doorStyleId: string }) {
  const mappings = useAsyncList(() => listStyleMappings(doorStyleId));
  const suppliers = useAsyncList(listSuppliers);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DooDoorStyleSupplierMapping | null>(null);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [styleName, setStyleName] = useState('');
  const [divest, setDivest] = useState(false);
  const [deprecatedDate, setDeprecatedDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deleteRow, setDeleteRow] = useState<DooDoorStyleSupplierMapping | null>(null);

  useEffect(() => {
    mappings.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doorStyleId]);

  const supplierName = (id: string, rows: DooSupplier[]) =>
    rows.find((row) => row.supplier_id === id)?.supplier_name || id;

  const openCreate = () => {
    setEditing(null);
    setSupplierId(null);
    setStyleName('');
    setDivest(false);
    setDeprecatedDate('');
    setError(null);
    setOpen(true);
  };

  return (
    <SectionCard
      title="Supplier mappings"
      subtitle="Link this Keystone style to each supplier's catalogue name."
      action={
        <Button size="sm" onClick={openCreate}>
          Add mapping
        </Button>
      }
    >
      {error ? <Alert color="warning" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {mappings.rows.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          No supplier mappings yet.
        </Typography>
      ) : (
        <OracleTable>
          <thead>
            <tr>
              <th>Supplier</th>
              <th>Supplier style name</th>
              <th>Status</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {mappings.rows.map((row) => (
              <tr key={row.mapping_id}>
                <td>{supplierName(row.supplier_id, suppliers.rows)}</td>
                <td>{row.supplier_style_name || '—'}</td>
                <td><StatusChip status={catalogueStatus(row)} /></td>
                <td>
                  <ActionRow>
                    <Button
                      size="sm"
                      variant="plain"
                      onClick={() => {
                        setEditing(row);
                        setSupplierId(row.supplier_id);
                        setStyleName(row.supplier_style_name || '');
                        setDivest(Boolean(row.divest));
                        setDeprecatedDate(row.deprecated_date?.slice(0, 10) || '');
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button size="sm" variant="plain" color="danger" onClick={() => setDeleteRow(row)}>
                      Remove
                    </Button>
                  </ActionRow>
                </td>
              </tr>
            ))}
          </tbody>
        </OracleTable>
      )}

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ minWidth: { xs: 'auto', sm: 440 } }}>
          <ModalClose />
          <Typography level="title-lg">{editing ? 'Edit mapping' : 'Add mapping'}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Field label="Supplier">
              <Select
                placeholder="Choose supplier"
                value={supplierId}
                onChange={(_, value) => setSupplierId((value as string) || null)}
              >
                {suppliers.rows.map((row) => (
                  <Option key={row.supplier_id} value={row.supplier_id}>
                    {row.supplier_name}
                  </Option>
                ))}
              </Select>
            </Field>
            <Field label="Supplier style name">
              <Input value={styleName} onChange={(e) => setStyleName(e.target.value)} />
            </Field>
            <DivestFields
              divest={divest}
              deprecatedDate={deprecatedDate}
              onDivest={setDivest}
              onDeprecatedDate={setDeprecatedDate}
            />
            <ActionRow sx={{ justifyContent: 'flex-end', mt: 0.5 }}>
              <Button variant="outlined" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!supplierId) {
                    setError('Choose a supplier.');
                    return;
                  }
                  try {
                    const payload = {
                      door_style_id: doorStyleId,
                      supplier_id: supplierId,
                      supplier_style_name: styleName.trim() || null,
                      divest,
                      deprecated_date: deprecatedDate || null,
                    };
                    if (editing) await updateStyleMapping(editing.mapping_id, payload);
                    else await createStyleMapping(payload);
                    setOpen(false);
                    setError(null);
                    mappings.reload();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Save failed');
                  }
                }}
              >
                Save
              </Button>
            </ActionRow>
          </Box>
        </ModalDialog>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteRow)}
        title="Remove mapping?"
        message={deleteRow ? `Remove the ${supplierName(deleteRow.supplier_id, suppliers.rows)} mapping?` : ''}
        onClose={() => setDeleteRow(null)}
        onConfirm={async () => {
          if (!deleteRow) return;
          await deleteStyleMapping(deleteRow.mapping_id);
          setDeleteRow(null);
          mappings.reload();
        }}
      />
    </SectionCard>
  );
}

function buildSupplierMappingLookups(
  mappings: DooDoorStyleSupplierMapping[],
  suppliers: DooSupplier[]
): {
  supplierNamesByDoorStyle: Map<string, string>;
  supplierStyleNamesByDoorStyle: Map<string, string>;
} {
  const supplierNameById = new Map(suppliers.map((row) => [row.supplier_id, row.supplier_name]));
  const supplierNames = new Map<string, string[]>();
  const supplierStyleNames = new Map<string, string[]>();

  mappings.forEach((mapping) => {
    const supplierName = supplierNameById.get(mapping.supplier_id);
    if (supplierName) {
      const current = supplierNames.get(mapping.door_style_id) ?? [];
      if (!current.includes(supplierName)) current.push(supplierName);
      supplierNames.set(mapping.door_style_id, current);
    }

    const styleName = mapping.supplier_style_name?.trim();
    if (styleName) {
      const current = supplierStyleNames.get(mapping.door_style_id) ?? [];
      if (!current.includes(styleName)) current.push(styleName);
      supplierStyleNames.set(mapping.door_style_id, current);
    }
  });

  return {
    supplierNamesByDoorStyle: new Map(
      Array.from(supplierNames.entries()).map(([doorStyleId, names]) => [doorStyleId, names.join(', ')])
    ),
    supplierStyleNamesByDoorStyle: new Map(
      Array.from(supplierStyleNames.entries()).map(([doorStyleId, names]) => [doorStyleId, names.join(', ')])
    ),
  };
}

function formatSizeRange(row: DooDoorStyle): string {
  const width = formatRange(row.min_width_mm, row.max_width_mm);
  const height = formatRange(row.min_height_mm, row.max_height_mm);
  if (!width && !height) return '—';
  if (!width) return height;
  if (!height) return width;
  return `${width} × ${height}`;
}

function formatRange(min: number | null, max: number | null): string {
  if (min != null && max != null) return `${min}–${max}`;
  if (min != null) return `${min}+`;
  if (max != null) return `≤${max}`;
  return '';
}

function toInt(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
